import { BaseController } from "@repo/baseCloud";

export interface MyViewBoard {
	id: number;
	title: string;
	sortIndex: number;
	includeInCycle: boolean;
}
export interface WidgetPosition {
	id: number;
	xPos: number;
	yPos: number;
	width: number;
	height: number;
}
export interface MyViewWidget extends WidgetPosition {
	boardId: number;
	title: string;
	widgetName: string;
	timeframe: number;
	configJson: string | null;
	enabled: boolean;
}
export interface WidgetFilter {
	brandIds?: number[];
	channelIds?: number[];
	marketerIds?: number[];
	usStateIds?: number[];
	branchIds?: number[];
	utilityIds?: number[];
	startDate?: string;
	endDate?: string;
}
export interface CustomParameter {
	name: string;
	prompt: string;
	dataType: number;
	defaultValue: unknown;
	readOnly: boolean;
}
export interface WidgetDefinition {
	widgetName: string;
	title: string;
	group: string;
	customParameters: CustomParameter[] | null;
	limitToChannelIds: number[] | null;
	noThreeMonthLimit: boolean;
	supportsUtilityFilter: boolean;
}
export interface Catalog {
	definitions: WidgetDefinition[];
	timeframes: { value: number; name: string }[];
}
export interface FilterOption {
	id: number;
	name: string;
}
export type FilterOptions = Record<
	"brands" | "channels" | "marketers" | "states" | "branches" | "utilities",
	FilterOption[]
>;
export interface WidgetData {
	data: unknown;
	refreshToken: string;
	unchanged: boolean;
}
export interface WidgetSettings {
	id: number;
	boardId: number;
	widgetName: string;
	title: string;
	timeframe: number;
	filter: WidgetFilter;
	customFieldsJson: string;
}
export interface ExportSchedule {
	enabled: boolean;
	frequency: number;
	hour: number;
	minute: number;
	meridiem: number;
	weekDays: number[] | null;
	weekOfTheMonth: number | null;
	dayOfTheMonth: number | null;
	monthlyFrequencyType: number | null;
}
export class MyViewController extends BaseController {
	static getExportSchedule = (id: number, board: boolean) =>
		MyViewController.Get<{
			schedule: ExportSchedule;
			timeZoneOffset: number;
			reactRendererEnabled: boolean;
			lastSentUtc: string | null;
		}>("ExportSchedule", {
			id,
			board,
			timeZoneOffset: new Date().getTimezoneOffset(),
		});
	static saveExportSchedule = (id: number, board: boolean, schedule: ExportSchedule, timeZoneOffset: number) =>
		MyViewController.Post("SaveExportSchedule", {}, { id, board, schedule, timeZoneOffset });
	static Get = BaseController.GetFn("/MVC/MyViewV2");
	static Post = BaseController.PostFn("/MVC/MyViewV2");
	static getBoards = () => MyViewController.Get<MyViewBoard[]>("Boards");
	static getWidgets = (boardId: number, signal?: AbortSignal) =>
		MyViewController.Get<MyViewWidget[]>("Widgets", { boardId }, { signal });
	static getCatalog = () => MyViewController.Get<Catalog>("Catalog");
	static getDispositions = (filter: WidgetFilter, signal?: AbortSignal) =>
		BaseController.GetFn("/MVC/GridFilters")<FilterOption[]>(
			"GetDispositions",
			{ ...filter, myViewOnly: true },
			{ signal },
		);
	static getFilters = (filter: WidgetFilter, signal?: AbortSignal) =>
		MyViewController.Get<FilterOptions>("Filters", filter, { signal });
	static getData = (id: number, refreshToken?: string, force = false, signal?: AbortSignal) =>
		MyViewController.Get<WidgetData>("Data", { id, refreshToken, force }, { signal });
	static saveBoard = (board: MyViewBoard) => MyViewController.Post<MyViewBoard>("SaveBoard", {}, { board });
	static deleteBoard = (id: number) => MyViewController.Post("DeleteBoard", {}, { id });
	static saveLayout = (boardId: number, positions: WidgetPosition[]) =>
		MyViewController.Post("Layout", {}, { boardId, positions });
	static saveWidget = (settings: WidgetSettings) => MyViewController.Post<{ id: number }>("SaveWidget", {}, settings);
	static deleteWidget = (id: number) => MyViewController.Post("DeleteWidget", {}, { id });
}
